import React, { useState, useEffect } from 'react';
import { Card, Select, Spin, Alert, Typography, Row, Col } from 'antd';
import { TrendLineChart } from './TrendLineChart';
import { ANALYTICS_API_URL } from '../../../../constants/config';
import { getAndSanitize } from '../../../../shared/utils/sanitization';

const { Title } = Typography;
const { Option } = Select;

interface TrendDataPoint {
  date: string;
  value: number;
  percentage?: number;
}

interface UsernameTrendData {
  approved: TrendDataPoint[];
  rejected: TrendDataPoint[];
  accuracy: TrendDataPoint[];
}

interface UsernameTrendChartProps {
  dateRange?: [string, string] | null;
  adminId?: string;
  loading?: boolean;
}

export const UsernameTrendChart: React.FC<UsernameTrendChartProps> = ({
  dateRange,
  adminId,
  loading: propLoading = false
}) => {
  const [data, setData] = useState<UsernameTrendData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'count' | 'accuracy'>('count');

  const fetchUsernameTrend = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = getAndSanitize("token");
      if (!token) {
        throw new Error("Token no disponible");
      }

      const params = new URLSearchParams();
      if (dateRange) {
        // Usar directamente las fechas ISO del filtro de Analytics
        params.append("dateFrom", dateRange[0]);
        params.append("dateTo", dateRange[1]);
      }
      if (adminId) {
        params.append("adminId", adminId);
      }

      const response = await fetch(
        `${ANALYTICS_API_URL}/api/analytics/parse-analytics/username-trend?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        setData(result.data);
      } else {
        console.error("UsernameTrendChart - API Error:", result);
        throw new Error(result.message || "Error al obtener datos de tendencia");
      }
    } catch (err: any) {
      console.error("Error fetching username trend:", err);
      setError(err.message || "Error al cargar tendencia de nombres de usuario");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsernameTrend();
  }, [dateRange, adminId]);

  const prepareChartData = () => {
    if (!data) return [];

    if (activeView === 'count') {
      const chartData: any[] = [];
      
      data.approved.forEach(point => {
        chartData.push({
          date: point.date,
          value: point.value,
          type: 'Aprobados'
        });
      });

      data.rejected.forEach(point => {
        chartData.push({
          date: point.date,
          value: point.value,
          type: 'Rechazados'
        });
      });

      return chartData;
    } else {
      const chartData = data.accuracy.map(point => ({
        date: point.date,
        value: point.value,
        type: 'Precisión (%)'
      }));
      return chartData;
    }
  };

  const hasData = () => {
    return data && (
      data.approved.length > 0 || 
      data.rejected.length > 0 || 
      data.accuracy.length > 0
    );
  };

  if (error) {
    return (
      <Card title="Tendencia de Análisis de Nombres de Usuario">
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
        />
      </Card>
    );
  }

  return (
    <Card
      title="Tendencia de Análisis de Nombres de Usuario"
      extra={
        <Select
          value={activeView}
          onChange={setActiveView}
          style={{ width: 160 }}
        >
          <Option value="count">Cantidad</Option>
          <Option value="accuracy">Precisión</Option>
        </Select>
      }
    >
      {(loading || propLoading) ? (
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Spin size="large" />
        </div>
      ) : hasData() ? (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <div style={{ textAlign: 'center' }}>
                <Title level={4} style={{ margin: 0, color: '#52c41a' }}>
                  {data!.approved.reduce((sum, item) => sum + item.value, 0)}
                </Title>
                <Typography.Text type="secondary">Total Aprobados</Typography.Text>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ textAlign: 'center' }}>
                <Title level={4} style={{ margin: 0, color: '#ff4d4f' }}>
                  {data!.rejected.reduce((sum, item) => sum + item.value, 0)}
                </Title>
                <Typography.Text type="secondary">Total Rechazados</Typography.Text>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ textAlign: 'center' }}>
                <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
                  {Math.round(
                    data!.accuracy.reduce((sum, item) => sum + item.value, 0) / 
                    (data!.accuracy.length || 1)
                  )}%
                </Title>
                <Typography.Text type="secondary">Precisión Promedio</Typography.Text>
              </div>
            </Col>
          </Row>
          
          <TrendLineChart
            data={prepareChartData()}
            xField="date"
            yField="value"
            seriesField={activeView === 'count' ? 'type' : undefined}
            height={400}
            smooth={true}
          />
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Typography.Text type="secondary">No hay datos disponibles</Typography.Text>
        </div>
      )}
    </Card>
  );
}; 